/**
 * Expose this module to global version of jquery using browserify-shim.
 *
 * @see ../../package.json
 * @see https://github.com/thlorenz/browserify-shim
 *
 * @since 1.0.1
 */
import $ from 'jquery';

/**
 * Show a tooltip when hovering over a glossary term link.
 *
 * @since 1.0.0
 */
const glossary = {
	/**
	 * Add settings saved to the window to this object.
	 */
	...window.emGlossary,

	/**
	 * Boolean to check if an xhr request is currently in progress.
	 */
	requestInProgress: false,

	/**
	 * Tooltip class and prefix for tooltip component classes.
	 *
	 * @since 1.0.1
	 *
	 * @type {String}
	 */
	tooltipClass: 'emg-tooltip',

	/**
	 * Class added to glossary term link to display the tooltip.
	 *
	 * @since 1.0.1
	 *
	 * @type {String}
	 */
	showTooltipClass: 'show-tooltip',

	/**
	 * Class added to glossary term link after the tooltip HTML has been added to the link.
	 * This prevents WP REST API requests being made for the same link more than once.
	 *
	 * @since 1.0.1
	 *
	 * @type {String}
	 */
	hasTooltipClass: 'has-tooltip',

	/**
	 * Class added to glossary term link after the tooltip content has been loaded.
	 *
	 * @since 1.0.1
	 *
	 * @type {String}
	 */
	tooltipLoadedClass: 'tooltip-loaded',

	/**
	 * Class added to glossary term link while the tooltip content is loading.
	 *
	 * @since 1.0.1
	 *
	 * @type {String}
	 */
	tooltipLoadingClass: 'tooltip-loading',

	/**
	 * Class added to glossary term link when the tooltip is going to appear above
	 * the window.emGlossaryOptions.topOffsetEl.
	 *
	 * @since 1.0.3
	 *
	 * @type {String}
	 */
	tooltipBottomClass: 'tooltip-bottom',

	/**
	 * Get all glossary term links on the page and add events.
	 *
	 * @since 1.0.0
	 * @since 1.0.1 Added focus, blur, and touchend event listeners for better accessibility and mobile support.
	 * @since 1.0.3 Added touchstart event to close the tooltip when the close button is touched.
	 */
	init() {
		this.termQuerySelector = `a[href*="/${this.postTypeSlug}/"]`;
		this.terms = document.querySelectorAll(this.termQuerySelector);
		if (this.terms.length > 0) {
			this.bindEvents();
			let i = 0;
			const l = this.terms.length;
			for (i; i < l; i++) {
				const term = this.terms[i];

				$(term).hoverIntent({
					over: this.handleShowTooltip,
					out: this.handleHideTooltip,
					timeout: 300,
				});

				/**
				 * Add class to terms for easy styling.
				 */
				term.classList.add('emg-term');

				/**
				 * Prevent default when clicking glossary terms.
				 */
				term.addEventListener('click', this.handleShowTooltip);

				/**
				 * Accessibility considerations.
				 */
				term.addEventListener('focus', this.handleShowTooltip);
				term.addEventListener('blur', this.handleHideTooltip);

				/**
				 * Touch-device considerations.
				 */
				document.addEventListener('touchstart', this.handleTouchStart);
				document.addEventListener('touchend', this.handleTouchEnd);

				/**
				 * Handle closing the tooltip on touch screens.
				 */
				$(document).on('touchstart', '.emg-tooltip__close', this.handleCloseClick);
			}
		}
	},

	/**
	 * Bind event handlers so they have access to this object using the 'this' keyword.
	 *
	 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Function/bind
	 *
	 * @since 1.0.0
	 */
	bindEvents() {
		this.handleShowTooltip = this.handleShowTooltip.bind(this);
		this.handleHideTooltip = this.handleHideTooltip.bind(this);
		this.handleTouchStart = this.handleTouchStart.bind(this);
		this.handleTouchEnd = this.handleTouchEnd.bind(this);
		this.handleCloseClick = this.handleCloseClick.bind(this);
	},

	/**
	 * Event handler that determines if the glossary link has a tooltip, and if not,
	 * makes a request to the WP REST API to get the tooltip data, and adds the tooltip markup.
	 * Then the tooltip is displayed to the user.
	 *
	 * @since 1.0.0
	 * @since 1.0.1 Showing the tooltip no matter what. Adding a "loading" class to the link in order to show an animation.
	 * @since 1.0.3 Added check to see if close button is clicked, and if so, bailing early in order to hide the tooltip.
	 * @since 1.0.4 Reference emGlossary.apiEndpoint instead of hard-coding the main site's endpoint.
	 *
	 * @param {Event} e
	 */
	handleShowTooltip(e) {
		e.preventDefault();
		const termEl = $(e.target).closest(this.termQuerySelector)[0];

		/**
		 * Bail early if we don't have a term element.
		 */
		if (!termEl || e.target.classList.contains('emg-tooltip__close')) {
			return;
		}

		/**
		 * Show the tooltip.
		 */
		this.showTooltip(termEl);

		/**
		 * Otherwise, make a request to the WP REST API to get the term data,
		 * and call this.loadTooltipContent on succes to add the tooltip markup to the link,
		 * and then show the tooltip.
		 */
		if (!termEl.classList.contains(this.tooltipLoadedClass)) {
			/**
			 * Add the loading class to the glossary term link.
			 */
			termEl.classList.add(this.tooltipLoadingClass);

			const pathnamePieces = termEl.pathname.split('/');
			const slug = encodeURIComponent(pathnamePieces[pathnamePieces.indexOf(this.postTypeSlug) + 1]);

			/**
			 * Make sure there isn't another XMLHttpRequest in progress before continuing.
			 */
			while (!this.requestInProgress) {
				/**
				 * Don't allow new requests to be made while this request is processing.
				 */
				this.requestInProgress = true;

				const xhr = new XMLHttpRequest();

				xhr.open('GET', `${this.apiEndpoint}?slug=${slug}`);

				xhr.onload = () => {
					if (xhr.status >= 200 && xhr.status < 400) {
						this.loadTooltipContent(termEl, xhr.responseText);
					} else {
						console.log(Error(xhr.statusText));
					}

					/**
					 * Allow new requests to be made. now that this request in finsihed.
					 */
					this.requestInProgress = false;
				};

				xhr.onerror = () => {
					console.log(Error('Network Error'));
				};

				xhr.send();
			}
		}
	},

	/**
	 * Hide the tooltip.
	 *
	 * @since 1.0.0
	 * @since 1.0.1 Using jQuery closest() instead of native closest() for IE11 support.
	 * @since 1.0.2 Added logic to position the tooltip properly on screens smaller than 480px wide.
	 *
	 * @param {Event} e
	 */
	handleHideTooltip(e) {
		const termEl = $(e.target).closest(this.termQuerySelector)[0];
		if (termEl) {
			this.hideTooltip(termEl);
		}
	},

	/**
	 * Store the page's Y position to check if the user has touchmoved.
	 *
	 * @since 1.0.1
	 *
	 * @param {Event} e
	 */
	handleTouchStart(e) {
		this.pageY = e.changedTouches[0].pageY;
	},

	/**
	 * Hide the currently open tooltip if it's not our event's target,
	 * it currently has the showTooltipClass applied, and the user hasn't
	 * performed a touchmove event.
	 *
	 * @since 1.0.1
	 *
	 * @param {Event} e
	 */
	handleTouchEnd(e) {
		/**
		 * Only proceed if there is an open tooltip, and it's not our event's target,
		 * and it currently has the showTooltipClass applied.
		 */
		if (e.changedTouches[0].pageY === this.pageY && this.openTooltip && this.openTooltip !== e.target && this.openTooltip.classList.contains(this.showTooltipClass)) {
			this.hideTooltip(this.openTooltip);
		}
	},

	/**
	 * Add the glossary term tooltip, containing title and definition, as a child of the passed element.
	 *
	 * @since 1.0.0
	 * @since 1.0.1 Changed function from addTooltip to loadTooltipContent. Checking for title and content
	 * before adding tooltip.
	 * @since 1.0.3 Running this.showTooltip(el) at the end of loadTooltipContent() in order to recalculate
	 * the tooltip's top position with its full height.
	 *
	 * @param {Node} el Element to add the tooltip to.
	 * @param {String} data JSON string from WP REST API request.
	 */
	loadTooltipContent(el, data) {
		data = JSON.parse(data)[0];
		if (data.title.rendered && data.content.rendered) {
			const tooltipInner = el.querySelector(`.${this.tooltipClass}__inner`);

			/**
			 * Add the tooltip content to the tooltip.
			 */
			tooltipInner.innerHTML += `<div class="${this.tooltipClass}__content">
				<div class="${this.tooltipClass}__title">${data.title.rendered}</div>
				${data.content.rendered}
			</div>`;

			/**
			 * Remove the loading class and add the loaded class.
			 */
			el.classList.remove(this.tooltipLoadingClass);
			el.classList.add(this.tooltipLoadedClass);

			this.showTooltip(el);
		}
	},

	/**
	 * Show the tooltip.
	 *
	 * @since 1.0.0
	 * @since 1.0.1 Check if tooltip markup has been added to the link before querying for it.
	 * @since 1.0.1 Setting left to 'auto', instead of '', if the tooltip is offscreen right.
	 * @since 1.0.3 Added conditional logic to detect if the tooltip is going to appear off the top of the screen,
	 * or over a user-defined topOffsetEl, which can be set on window.emGlossaryOptions.topOffsetEl.
	 *
	 * @param {Node} el Element containing the tooltip to show.
	 */
	showTooltip(el) {
		/**
		 * Add tooltip markup if it hasn't already been added.
		 */
		if (!el.classList.contains(this.hasTooltipClass)) {
			el.innerHTML += `<div class="${this.tooltipClass}"><div class="${this.tooltipClass}__inner"></div><div class="${this.tooltipClass}__close"></div></div>`;
			el.classList.add(this.hasTooltipClass);
		}

		const tooltip = el.querySelector(`.${this.tooltipClass}`);

		/**
		 * Reset tooltip styles to defaults.
		 */
		tooltip.style.cssText = '';

		/**
		 * Make sure the tooltip isn't bleeding off the edges of the screen,
		 * and if it is, bring it back into view.
		 */
		const tooltipRect = tooltip.getBoundingClientRect();
		if (window.innerWidth <= 480) {
			tooltip.style.marginLeft = `${-(tooltipRect.left) + 20}px`;
		} else if (tooltipRect.left < 20) {
			tooltip.style.left = 0;
			tooltip.style.transform = 'none';
		} else if (tooltipRect.left + tooltipRect.width > window.innerWidth) {
			tooltip.style.left = 'auto';
			tooltip.style.right = 0;
			tooltip.style.transform = 'none';
		}

		if (!el.classList.contains(this.tooltipBottomClass)) {
			const topOffsetEl = window.emGlossaryOptions ? window.emGlossaryOptions.topOffsetEl : false;
			const topOffsetHeight = (topOffsetEl ? topOffsetEl.clientHeight : 0) + 15;

			if (tooltipRect.top >= topOffsetHeight) {
				el.classList.remove(this.tooltipBottomClass);
			} else {
				el.classList.add(this.tooltipBottomClass);
			}
		}

		el.classList.add(this.showTooltipClass);
		this.openTooltip = el;
	},

	/**
	 * Hide the tooltip.
	 *
	 * @since 1.0.0
	 *
	 * @param {Node} el Element containing the tooltip to hide.
	 */
	hideTooltip(el) {
		if (!el) {
			return;
		}

		el.classList.remove(this.showTooltipClass);

		setTimeout(() => {
			el.classList.remove(this.tooltipBottomClass);
		}, 300);
	},

	/**
	 * Close the tooltip when the close button is touched.
	 *
	 * @since 1.0.3
	 *
	 * @param {Event} e
	 */
	handleCloseClick() {
		const $closeButton = $(this);
		const $glossaryLink = $closeButton.closest(this.termQuerySelector);
		this.hideTooltip($glossaryLink[0]);
	},
};

glossary.init();

/**
 * Re-run on gravity forms confirmations.
 */
let confirmationCount = 1;
$(document).on('gform_confirmation_loaded', () => {
	/**
	 * Seems to run twice, so added a check to only run once.
	 */
	if (confirmationCount === 1) {
		glossary.init();
		confirmationCount++;
	}
});
